import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Chrome, 
  Body as ChromeBody, 
  Header, 
  Main, 
  Content,
  SkipNav 
} from '@zendeskgarden/react-chrome';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/auth';
import Sidebar from './Sidebar';

interface LayoutItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface DashboardLayoutProps {
  title: string;
  userInfo: string;
  activeTab: string;
  menuItems: LayoutItem[];
  onTabChange: (tabId: string) => void;
  children: React.ReactNode;
  breadcrumb?: string;
  userName?: string;
  userType?: string;
  userAvatar?: string;
  pageDescription?: string;
}

const StyledChrome = styled(Chrome)`
  height: 100vh;
  height: -webkit-fill-available;
  background-color: #f8f9fa;
  width: 100%;
  min-width: 320px;
  display: flex;
  overflow: hidden;
  
  @media (max-width: 768px) {
    flex-direction: column;
    position: relative;
    min-height: 100vh;
    min-height: -webkit-fill-available;
  }
`;

const StyledHeader = styled(Header)`
  background: white !important;
  border: none !important;
  box-shadow: none !important;
  margin-left: 16px;
  margin-right: 16px;
  margin-top: 16px;
  border-bottom: 1px solid #e9ebed !important;
  height: 60px;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    margin: 0;
    padding: 0 16px;
    height: 70px;
    border-radius: 0;
    border-bottom: 1px solid #e9ebed !important;
    position: relative;
    z-index: 100;
    justify-content: space-between;
  }
  
  @media (max-width: 480px) {
    height: 60px;
    padding: 0 12px;
  }
`;

const StyledContent = styled(Content)`
  margin-left: 16px;
  margin-right: 16px;
  margin-bottom: 16px;
  margin-top: 16px;
  flex: 1;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Hide scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
  
  @media (max-width: 768px) {
    margin: 0;
    flex: 1;
    width: 100%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }
`;

const StyledMain = styled(Main)`
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e9ebed;
  width: 100%;
  flex: 1;
  overflow: hidden;
  
  @media (max-width: 768px) {
    border-radius: 0;
    border: none;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }
`;

const PageHeader = styled.div`
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #e9ebed;
`;

const PageTitle = styled.h1`
  margin: 0 0 8px 0;
  color: #2f3941;
  font-size: 28px;
  font-weight: 600;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const PageDescription = styled.p`
  margin: 0;
  color: #68737d;
  font-size: 16px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.5;
`;

const HeaderTitle = styled.span`
  margin: 0;
  color: #68737d;
  font-size: 14px;
  font-weight: 400;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const UserProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #8D4F1B;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const UserName = styled.span`
  color: #2f3941;
  font-size: 14px;
  font-weight: 500;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const UserType = styled.span`
  color: #68737d;
  font-size: 12px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const MainContentWrapper = styled.div`
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const MobileHeaderLogo = styled.img`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    height: 32px;
    width: auto;
  }
  
  @media (max-width: 480px) {
    height: 28px;
  }
`;

const MobileToggleButton = styled.button`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    align-items: center;
    justify-content: center;
    color: #8D4F1B;
    
    &:hover {
      background: rgba(141, 79, 27, 0.1);
      border-radius: 4px;
    }
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 6px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const DesktopHeaderContent = styled.div`
  display: flex;
  align-items: center;
  padding: 0 24px;
  flex: 1;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopUserSection = styled.div`
  display: flex;
  align-items: center;
  padding: 0 24px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileHeaderContent = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;


const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  title,
  userInfo,
  activeTab,
  menuItems,
  onTabChange,
  children,
  breadcrumb,
  userName,
  userType,
  userAvatar,
  pageDescription
}) => {
  const { user, logout } = useAuth();
  const [navExpanded, setNavExpanded] = useState(window.innerWidth >= 768);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const navigate = useNavigate();

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setNavExpanded(true);
        setSidebarExpanded(false); // Close mobile sidebar on desktop
      } else {
        setNavExpanded(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const handleLogout = async () => {
    try {
      await logout(); // Use auth context logout
      // The logout function already handles navigation via window.location.href
    } catch (error) {
      console.error('Logout failed:', error);
      // Force logout even if API call fails
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  const handleProfileClick = () => {
    const userRole = user?.role || 'admin';
    switch (userRole) {
      case 'admin':
      case 'super-admin':
        navigate('/admin/profile');
        break;
      case 'physician':
        navigate('/physician/profile');
        break;
      case 'specialist':
        navigate('/reviewer/profile');
        break;
      default:
        navigate('/profile');
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <StyledChrome>
      <SkipNav targetId="main-content">Skip to main content</SkipNav>
      
      <Sidebar
        items={menuItems}
        activeItem={activeTab}
        onItemClick={onTabChange}
        onLogout={handleLogout}
        userInfo={userInfo}
        isExpanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded(!sidebarExpanded)}
      />

      <ChromeBody>
        <StyledHeader>
          <DesktopHeaderContent>
            <HeaderTitle>Dashboard {'>'} {breadcrumb || title}</HeaderTitle>
          </DesktopHeaderContent>
          
          <MobileHeaderContent>
            <MobileToggleButton onClick={toggleSidebar}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
              </svg>
            </MobileToggleButton>
            <MobileHeaderLogo src="/dermAI_logo.svg" alt="DermAI Logo" />
            <div style={{ width: '40px' }} /> {/* Spacer for balance */}
          </MobileHeaderContent>
          
          <DesktopUserSection>
            <UserProfileSection onClick={handleProfileClick}>
              <UserAvatar>
                {userAvatar ? (
                  <img src={userAvatar} alt="User Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                ) : (
                  getInitials(userName || userInfo || 'User')
                )}
              </UserAvatar>
              <UserDetails>
                <UserName>{userName || userInfo || 'User'}</UserName>
                <UserType>{userType || 'User'}</UserType>
              </UserDetails>
            </UserProfileSection>
          </DesktopUserSection>
        </StyledHeader>
        
        <StyledContent>
          <StyledMain id="main-content">
            <MainContentWrapper>
              {children}
            </MainContentWrapper>
          </StyledMain>
        </StyledContent>
      </ChromeBody>
    </StyledChrome>
  );
};

export default DashboardLayout;
